import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { requestChangeHref } from '../../utils/request-change';
import {
  Badge,
  DataGrid,
  EmptyState,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-fee-reconciliation';
import { Receipt } from 'lucide-react';
import { ListHeader } from '../collateral/CollateralListParts';
import { usePayments, useReconciliationReport } from '../../hooks/useUccFees';
import { formatCents, formatDate, NOT_RECORDED } from './format';

type FeePayment = components['schemas']['FeePayment'];
type FeePaymentStatus = components['schemas']['FeePaymentStatus'];

const PAGE_SIZE = 20;
const WINDOW_DAYS = 90;

/**
 * Label and badge variant per FeePaymentStatus. Keyed on the generated union so
 * a status added to ucc-fee-reconciliation.openapi.yaml fails tsc here until it
 * has a label, instead of printing its raw code.
 */
export const PAYMENT_STATUS: Record<
  FeePaymentStatus,
  { label: string; variant: 'success' | 'warning' | 'error' | 'neutral' | 'info' }
> = {
  pending: { label: 'Pending', variant: 'warning' },
  confirmed: { label: 'Confirmed', variant: 'info' },
  reconciled: { label: 'Reconciled', variant: 'success' },
  refundPending: { label: 'Refund pending', variant: 'warning' },
  refunded: { label: 'Refunded', variant: 'neutral' },
  disputed: { label: 'Disputed', variant: 'error' },
};

function shortId(id: string | undefined): string {
  return id ? `${id.slice(0, 8)}...` : NOT_RECORDED;
}

export function FeeReconciliation() {
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  const [page, setPage] = useState(1);

  // Compute the report window ONCE per mount. Computing it inline each render
  // produced ever-changing ISO timestamps, which made the query keys unstable
  // -> react-query refetched on every render -> setState -> re-render ->
  // refetch (infinite loop hammering /api/v1/ucc-fees/fee-reconciliation/report).
  // useMemo([]) freezes the window. The payment table reads the same window, so
  // the cards and the rows describe one period.
  const { fromDate, toDate } = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - WINDOW_DAYS * 24 * 60 * 60 * 1000);
    return { fromDate: from.toISOString(), toDate: to.toISOString() };
  }, []);

  const paymentsQuery = usePayments({
    tenantId,
    fromDate,
    toDate,
    page,
    limit: PAGE_SIZE,
  });
  const reportQuery = useReconciliationReport({ tenantId, fromDate, toDate });

  const payments: FeePayment[] = paymentsQuery.data?.data ?? [];
  const totalCount = paymentsQuery.data?.count ?? 0;

  // The summary counts and the money total come from the report's `summary`
  // block. While it loads, or when it failed, each card prints '--' with the
  // reason; an absent report is never shown as a measured 0 or $0.00.
  const summary = reportQuery.data?.summary;
  const summaryCaption = summary
    ? undefined
    : reportQuery.isError
      ? METRIC_READ_CAPTIONS.failed
      : METRIC_READ_CAPTIONS.unread;
  const count = (n: number | undefined) => (summary && n != null ? n.toLocaleString('en-US') : NOT_RECORDED);

  const columns: ColumnDef<FeePayment, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'Payment',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary">{shortId(row.original.id)}</span>
        ),
      },
      {
        accessorKey: 'filingId',
        header: 'Filing',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary">{shortId(row.original.filingId)}</span>
        ),
      },
      {
        accessorKey: 'jurisdictionCode',
        header: 'Jurisdiction',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary">
            {row.original.jurisdictionCode || NOT_RECORDED}
          </span>
        ),
      },
      {
        accessorKey: 'amountCents',
        header: 'Amount',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums">
            {row.original.amountCents != null
              ? formatCents(row.original.amountCents, row.original.currency || 'USD')
              : NOT_RECORDED}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const entry = status ? PAYMENT_STATUS[status] : undefined;
          return entry ? (
            <Badge variant={entry.variant} size="sm">
              {entry.label}
            </Badge>
          ) : (
            <span className="text-sm text-tertiary">{NOT_RECORDED}</span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Recorded',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary tabular-nums">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
    ],
    [],
  );

  const refresh = () => {
    void paymentsQuery.refetch();
    void reportQuery.refetch();
  };
  const refreshing = paymentsQuery.isFetching || reportQuery.isFetching;

  return (
    <div>
      <ListHeader
        onRefresh={refresh}
        refreshing={refreshing}
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Read-only view of your institution's UCC filing fee payments and
            reconciliation status against the platform-managed fee schedule
            ({WINDOW_DAYS}-day window).{' '}
            {/* GH #1962: was "Contact Aaim platform support to dispute a charge."
                -- a dead end. Now files a tracked in-app request. */}
            <Link
              to={requestChangeHref({
                surface: 'UCC fee reconciliation',
                item: 'Dispute a fee charge',
                detail:
                  'Fee schedule is platform-managed; disputes accepted within the 90-day window',
              })}
              className="text-accent hover:underline"
            >
              Dispute a charge
            </Link>
          </p>
        }
      />

      <MetricCardGrid columns={3} className="mb-6 lg:grid-cols-5">
        <MetricCard label="Payments" value={count(summary?.paymentCount)} caption={summaryCaption} />
        <MetricCard label="Reconciled" value={count(summary?.reconciledCount)} caption={summaryCaption} />
        <MetricCard label="Pending" value={count(summary?.pendingCount)} caption={summaryCaption} />
        <MetricCard label="Refunded" value={count(summary?.refundedCount)} caption={summaryCaption} />
        <MetricCard
          label="Total paid"
          value={summary ? formatCents(summary.totalPaidCents) : NOT_RECORDED}
          caption={summaryCaption}
        />
      </MetricCardGrid>

      <DataGrid<FeePayment>
        data={payments}
        columns={columns}
        isLoading={paymentsQuery.isLoading}
        errorMessage={
          paymentsQuery.isError
            ? `Fee payments could not be read: ${(paymentsQuery.error as Error).message}`
            : undefined
        }
        emptyState={
          <EmptyState
            icon={<Receipt className="h-8 w-8" />}
            title={`No filing fee payments in the last ${WINDOW_DAYS} days`}
            description="A payment appears here once a filing fee is recorded against one of your institution's UCC filing submissions."
          />
        }
        pagination={{
          page: page - 1,
          pageSize: PAGE_SIZE,
          total: totalCount,
          onPageChange: (next) => setPage(next + 1),
        }}
        getRowId={(row) => row.id}
        hoverable
      />
    </div>
  );
}
